import { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { CourierProviderModel } from "../../models/courier-provider/courier-provider-model";
import { CourierProviderNewModel } from "../../models/courier-provider/courier-provider-new-model";
import Helpers from "../../utils/helpers";
import { OverlayPanel } from "primereact/overlaypanel";
import { Message } from "primereact/message";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { fetchCourierProviderData } from "../../redux/slices/courier-provider/courierProviderActions";
import { CriteriaChecker } from "./courier-criteria";
import ProductStorageHelpers from "../../utils/product-local-storage-wrapper";
import { ProductCheckoutModel } from "../../models/products/product-checkout-model";

interface CourierProviderListProps {
  isProcessed: (isProcessData: boolean) => void;
  isBtnShow: (isTrue: boolean) => void;
  onProvidersAvailable: (hasProviders: boolean) => void;
  onProviderSelected: (isSelected: boolean) => void;
}

const CourierProviderList = ({
  isProcessed,
  isBtnShow,
  onProvidersAvailable,
  onProviderSelected,
}: CourierProviderListProps) => {
  const [selectedCourierProvider, setSelectedCourierProvider] = useState<CourierProviderNewModel | null>(null);
  const isAdmin = useAppSelector((state) => state.isAdmin);
  const [rowClick, setRowClick] = useState<boolean>(true);
  const op = useRef<any>();
  const dispatch = useAppDispatch();
  const defaultList: CourierProviderNewModel[] = [
    {
      courierProviderID: 0,
      providerName: "Home Delivery",
      stateID: "0",
      stateName: "",
      criteria: "0",
      amount: 0,
    },
  ];

  const [courierProviderData, setCourierProviderData] = useState<CourierProviderNewModel[]>([]);

  const courierData = useAppSelector((state) => state.courierProviders.data);
  const loading = useAppSelector((state) => state.courierProviders.loading);
  const error = useAppSelector((state) => state.courierProviders.error);

  const selectedUserAddress = useAppSelector((state) => state.userAddress);

  const serializedWeights = localStorage.getItem("total-weight");
  let totalWeights = 0;
  if (serializedWeights !== null) {
    totalWeights = JSON.parse(serializedWeights.toString());
  }

  const adjustedWeight = Math.max(Math.ceil(totalWeights), 1);

  const loadSelectedCourierProvider = () => {
    if (isAdmin) {
      const serializedprovider = localStorage.getItem("courier-provider");
      if (serializedprovider !== null) {
        setSelectedCourierProvider(JSON.parse(serializedprovider));
      }
    } else {
      localStorage.removeItem("courier-provider");
    }
  };

  useEffect(() => {
    if (courierData && courierData.length > 0) {
      const filteredData = courierData.filter(
        (item) =>
          item.stateName === selectedUserAddress.selecedUserAddresses?.stateName &&
          item.criteria === CriteriaChecker(totalWeights)
      );

      const productList = ProductStorageHelpers.GetProductStorage();

      const hasCourierUnavailableProducts = productList.some(
        (product: ProductCheckoutModel) => product.courierAvailable === false
      );

      const allProductsHaveHomeDelivery = productList.every(
        (product: ProductCheckoutModel) => product.isHomeDeliveryAvailable === true
      );

      const homeDeliveryAvailableForAddress = selectedUserAddress.selecedUserAddresses?.homeDeliveryAvailable;

      let providersToShow: CourierProviderNewModel[] = [];
      let processedStatus = false;

      if (hasCourierUnavailableProducts) {
        if (allProductsHaveHomeDelivery && homeDeliveryAvailableForAddress) {
          providersToShow = defaultList;
          processedStatus = true;
        } else {
          providersToShow = [];
          processedStatus = true;
        }
      } else if (allProductsHaveHomeDelivery && homeDeliveryAvailableForAddress) {
        providersToShow = defaultList;
        processedStatus = true;
      } else {
        providersToShow = filteredData;
        processedStatus = !homeDeliveryAvailableForAddress;
      }

      setCourierProviderData(providersToShow);
      isProcessed(processedStatus);
      onProvidersAvailable(providersToShow.length > 0);
      loadSelectedCourierProvider();
      isBtnShow(false);

      // ✅ Auto-select courier provider if only one available
      if (providersToShow.length === 1) {
        const onlyProvider = providersToShow[0];
        setSelectedCourierProvider(onlyProvider);

        const convertedProvider: CourierProviderModel = {
          courierProviderId: onlyProvider.courierProviderID,
          courierProviderName: onlyProvider.providerName,
          weightSlab: onlyProvider.criteria,
          charges: onlyProvider.amount,
        };

        saveCourierProvider(convertedProvider);
        isBtnShow(true);
        onProviderSelected(true);
      }
    }
  }, [courierData, selectedUserAddress.selecedUserAddresses, totalWeights]);

  useEffect(() => {
    dispatch(fetchCourierProviderData(""));
  }, [dispatch]);

  useEffect(() => {
    if (selectedCourierProvider) {
      const updatedProvider = courierProviderData.find(
        (provider) => provider.courierProviderID === selectedCourierProvider.courierProviderID
      );

      if (updatedProvider) {
        setSelectedCourierProvider({
          ...updatedProvider,
          amount: updatedProvider.amount,
        });
      } else {
        setSelectedCourierProvider(null);
      }
      isBtnShow(true);
    }
  }, [courierProviderData, adjustedWeight]);

  useEffect(() => {
    if (selectedCourierProvider === null) {
      localStorage.setItem("courier-charges", JSON.stringify(0));
    } else {
      const courierCharges =
        selectedCourierProvider.amount * adjustedWeight + selectedCourierProvider.amount;
      localStorage.setItem("courier-charges", JSON.stringify(courierCharges));
      localStorage.setItem("courier-provider", JSON.stringify(selectedCourierProvider));
    }
  }, [selectedCourierProvider, adjustedWeight]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  const providerTemplate = (slab: any) => {
    if (slab.providerName === "Home Delivery") {
      return (
        <img
          src={process.env.PUBLIC_URL + "/assests/images/home-delivery-service.png"}
          alt={slab.providerName}
          style={{ width: 120 }}
        />
      );
    } else if (slab.providerName === "Professional Courier") {
      return (
        <img
          src={process.env.PUBLIC_URL + "/assests/images/professinal-courier01.jpg"}
          alt={slab.providerName}
          style={{ width: 120 }}
        />
      );
    } else if (slab.providerName === "DTDC") {
      return (
        <img
          src={process.env.PUBLIC_URL + "/assests/images/dtdc.png"}
          alt={slab.providerName}
          style={{ width: 120 }}
        />
      );
    } else if (slab.providerName === "VRL Service") {
      return (
        <>
          <img
            src={process.env.PUBLIC_URL + "/assests/images/vrl-service-new.png"}
            alt={slab.providerName}
            style={{ width: 120 }}
          />
        </>
      );
    }
  };

  const calculateTemplate = (slab: any) => {
    const courierCharges = slab.amount * adjustedWeight + slab.amount;
    return <span className="font-bold">{Helpers.formatAmountInINR(courierCharges)}</span>;
  };

  const showMessage = (e: any, data: any) => {
    if (data.providerName === "VRL Service") {
      op.current.toggle(e);
    }
  };

  const onRowSelect = (event: any) => {
    saveCourierProvider(event.data);
    isBtnShow(event.data != null ? true : false);
    onProviderSelected(true);
  };

  const onRowUnselect = (event: any) => {
    localStorage.removeItem("courier-provider");
    const courierCharges = 0;
    localStorage.setItem("courier-charges", JSON.stringify(courierCharges));
    isBtnShow(false);
    onProviderSelected(false);
  };

  const saveCourierProvider = (state: CourierProviderModel) => {
    try {
      const serializedState = JSON.stringify(state);
      localStorage.setItem("courier-provider", serializedState);
    } catch (err) {
      console.error(err);
    }
  };

  const isSelectable = (data: CourierProviderModel) => data.courierProviderId === 0;
  const rowClassName = (data: CourierProviderModel) => (isSelectable(data) ? "p-disabled" : "");

  return (
    <>
      {courierProviderData !== null ? (
        <DataTable
          value={courierProviderData}
          selectionMode={rowClick ? undefined : "radiobutton"}
          selection={selectedCourierProvider!}
          onSelectionChange={(e: any) => setSelectedCourierProvider(e.value)}
          dataKey="courierProviderID"
          tableStyle={{ minWidth: "10rem" }}
          size="small"
          showGridlines
          onRowSelect={onRowSelect}
          onRowUnselect={onRowUnselect}
          onRowClick={(e) => showMessage(e.originalEvent, e.data)}
        >
          <Column selectionMode="single" headerStyle={{ width: "3rem" }}></Column>
          <Column field="providerName" header="Courier Provider" body={providerTemplate}></Column>
          <Column field="amount" header="Courier Charges" body={calculateTemplate}></Column>
        </DataTable>
      ) : null}
      <OverlayPanel ref={op}>
        <Message text="Customer need to pick up from nearest VRL Godown." />
      </OverlayPanel>
    </>
  );
};

export default CourierProviderList;
